import { Box, Checkbox, FormControlLabel, Typography } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react'
import { FormSelect, FormTextField } from '../../../../components';
import { QUOTAS_INTERVAL_API_PLANS } from '../../../../constants';
import PropTypes from 'prop-types';
import clsx from 'clsx';

const MAX_LIMIT = 2 * 1000 * 1000 * 1000;

const OverView = (props) => {
  const {
    intl,
    classes,
    currentTab,
    payload,
    setPayload,
    apiPlanDetails,
    apiPlanUuid,
    enableQuotaCheck,
    setEnableQuotaCheck,
    enableRateLimitCheck,
    setEnableRateLimitCheck,
  } = props

  const [rateLimitFieldEmptyError, setRateLimitFieldEmptyError] = useState(false);
  const [rateLimitFieldLimitError, setRateLimitFieldLimitError] = useState(false);
  const [quotaFieldEmptyError, setQuotaFieldEmptyError] = useState(false);
  const [quotaFieldEmptyLimitError, setQuotaFieldEmptyLimitError] = useState(false);
  const [quotaLimitExceedsFieldError, setQuotaLimitExceedsFieldError] = useState(false);

  const handleChange = (fieldName, value) => {
    setPayload({ ...payload, [fieldName]: value });

    if (fieldName === "rateLimit") {
      if (value !== "" && payload.quota !== "" && enableRateLimitCheck && enableQuotaCheck) {
        setQuotaLimitExceedsFieldError(Number(value) >= Number(payload.quota));
      } else {
        setQuotaLimitExceedsFieldError(false);
      }
      if (value < 1 || value > MAX_LIMIT) {
        setRateLimitFieldLimitError(true);
      } else {
        setRateLimitFieldLimitError(false);
      }
      setRateLimitFieldEmptyError(value === "");
    } else if (fieldName === "quota") {
      if (payload.rateLimit !== "" && value !== "" && enableQuotaCheck && enableRateLimitCheck) {
        setQuotaLimitExceedsFieldError(Number(payload.rateLimit) >= Number(value));
      } else {
        setQuotaLimitExceedsFieldError(false);
      }
      if (value < 1 || value > MAX_LIMIT) {
        setQuotaFieldEmptyLimitError(true);
      } else {
        setQuotaFieldEmptyLimitError(false);
      }
      setQuotaFieldEmptyError(value === "");
    }
  };

  const handleBlur = (fieldName) => {
    if (fieldName === "rateLimit" && payload.rateLimit === "") {
      setRateLimitFieldEmptyError(true);
      setRateLimitFieldLimitError(false);
    } else if (fieldName === "quota" && payload.quota === "") {
      setQuotaFieldEmptyError(true);
      setQuotaFieldEmptyLimitError(false);
    }
  }

  const emptyFieldMessage = <span className={classes.textError}>{intl.getI18nMessage("error.field.mandatory")}</span>;
  const fieldLimitExceedsMessage = <span className={classes.textError}>{intl.getI18nMessage("label.api.plans.add.quota.exceeds.error.message")}</span>;
  const limitErrorMessage = <span className={classes.textError}>{intl.getI18nMessage("label.api.plans.add.overview.rate.limit.help.text")}</span>;

  const showRateLimitHelperText = useMemo(() => {
    if (rateLimitFieldEmptyError) {
      return emptyFieldMessage;
    }
    if (quotaLimitExceedsFieldError) {
      return fieldLimitExceedsMessage;
    }
    if (rateLimitFieldLimitError) {
      return limitErrorMessage;
    }
    return intl.getI18nMessage("label.api.plans.add.overview.rate.limit.help.text");
  }, [rateLimitFieldEmptyError, quotaLimitExceedsFieldError, rateLimitFieldLimitError, classes]);

  const showQuotaHelperText = useMemo(() => {
    if (quotaFieldEmptyError) {
      return emptyFieldMessage;
    }
    if (quotaLimitExceedsFieldError) {
      return fieldLimitExceedsMessage;
    }
    if (quotaFieldEmptyLimitError) {
      return limitErrorMessage;
    }
    return intl.getI18nMessage("label.api.plans.add.overview.rate.limit.help.text");
  }, [quotaFieldEmptyError, quotaLimitExceedsFieldError, quotaFieldEmptyLimitError, classes]);

  useEffect(() => {
    if (currentTab === 'add-api-plans-overview-tab') {
      setRateLimitFieldEmptyError(false);
      setQuotaFieldEmptyError(false);
      setQuotaLimitExceedsFieldError(false);
      setRateLimitFieldLimitError(false);
      setQuotaFieldEmptyLimitError(false);
    }
  }, [currentTab]);

  useEffect(() => {
    if (apiPlanDetails && apiPlanUuid) {
      if (apiPlanDetails?.rateLimit) {
        setEnableRateLimitCheck(true);
      } else {
        setEnableRateLimitCheck(false);
      }
      if (apiPlanDetails?.quota) {
        setEnableQuotaCheck(true);
      } else {
        setEnableQuotaCheck(false);
      }
    }
  }, [apiPlanDetails, apiPlanUuid])

  const handleEnableQuotaCheck = (e) => {
    const value = e.target.checked;
    setEnableQuotaCheck(value);
    if (value && !payload.quota) {
      setQuotaFieldEmptyError(true);
    }

    if (!value) {
      setQuotaLimitExceedsFieldError(false)
    }

    if (value && payload.quota !== "" && payload.rateLimit !== "") {
      setQuotaLimitExceedsFieldError(Number(payload.rateLimit) >= Number(payload.quota));
    }
  }

  const handleEnableRateLimitCheck = (e) => {
    const value = e.target.checked;
    setEnableRateLimitCheck(value);
    if (value && !payload.rateLimit) {
      setRateLimitFieldEmptyError(true);
    }

    if (!value) {
      setQuotaLimitExceedsFieldError(false)
    }

    if (value && payload.quota !== "" && payload.rateLimit !== "") {
      setQuotaLimitExceedsFieldError(Number(payload.rateLimit) >= Number(payload.quota));
    }
  }

  const overViewTabContentClass = clsx(classes.flexNonAlign, classes.overViewTabContent,
    classes.flexWrap);

  return (
    <div className={overViewTabContentClass}>
      <Box>
        <Typography variant='h4'>{intl.getI18nMessage("label.rate.quota.limit")}</Typography>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                id='add-api-plan-enable-rate-limit-checkbox'
                data-apim-test='add-api-plan-enable-rate-limit-checkbox'
                onChange={(e) => handleEnableRateLimitCheck(e)}
                checked={enableRateLimitCheck}
              />
            }
            label={
              <>
                <Typography className={classes.rateLimitQuotaLabel}>{intl.getI18nMessage("label.rate.quota.limit.rate.text")}</Typography>
              </>
            }
          />
          {
            enableRateLimitCheck &&
            <FormTextField
              id='add-api-plan-enable-rate-limit-field'
              name={intl.getI18nMessage("label.rate.quota.rate.limit")}
              fieldContainerClass={`${classes.overViewFieldsContainer} ${classes.rateLimitContainer}`}
              handleChange={(value) => handleChange('rateLimit', value)}
              type={'number'}
              value={payload?.rateLimit}
              helperText={showRateLimitHelperText}
              error={
                rateLimitFieldEmptyError ||
                quotaLimitExceedsFieldError ||
                rateLimitFieldLimitError
              }
              handleBlur={() => handleBlur('rateLimit')}
            />
          }
        </div>

        <div>
          <FormControlLabel
            control={
              <Checkbox
                id='add-api-plan-enable-quota-checkbox'
                data-apim-test='add-api-plan-enable-quota-checkbox'
                onChange={(e) => handleEnableQuotaCheck(e)}
                checked={enableQuotaCheck}

              />
            }
            label={
              <>
                <Typography className={classes.rateLimitQuotaLabel}>{intl.getI18nMessage("label.rate.quota.limit.quota")}</Typography>
              </>
            }
          />
          {
            enableQuotaCheck &&
            <div className={classes.quotaContainer}>
              <FormSelect
                id='add-api-plan-enable-quota-duration-field'
                name={intl.getI18nMessage("label.rate.quota.limit.quota.interval")}
                data={QUOTAS_INTERVAL_API_PLANS}
                fieldContainerClass={classes.overViewFieldsContainer}
                handleChange={(e) => handleChange('quotaInterval', e.target.value)}
                value={payload?.quotaInterval}
                noNoneOption
              />
              <FormTextField
                id='add-api-plan-enable-quota-field'
                name={intl.getI18nMessage("label.rate.quota.limit.quota")}
                fieldContainerClass={classes.overViewFieldsContainer}
                handleChange={(value) => handleChange('quota', value)}
                type={'number'}
                value={payload?.quota}
                helperText={showQuotaHelperText}
                error={
                  quotaFieldEmptyError ||
                  quotaLimitExceedsFieldError ||
                  quotaFieldEmptyLimitError
                }
                handleBlur={() => handleBlur('quota')}
              />
            </div>
          }
        </div>
      </Box>

      <Box>
        <Typography variant='h4'>{intl.getI18nMessage("label.application.details.smalltitle.details")}</Typography>
        <FormTextField
          id='add-api-plan-description-field'
          fieldContainerClass={classes.apiPlansDescription}
          name={intl.getI18nMessage('label.rate.quota.description')}
          helperText={intl.getI18nMessage("label.description.help")}
          placeholder={intl.getI18nMessage("label.api.plans.add.description.placeholder.text")}
          handleChange={(value) => handleChange("description", value)}
          value={payload?.description}
          rows={5}
          maxLength="255"
          multiline={true}
          optional
        />
      </Box>
    </div>
  )
}

OverView.propTypes = {
  intl: PropTypes.object,
  classes: PropTypes.object,
  currentTab: PropTypes.string,
  payload: PropTypes.object,
  setPayload: PropTypes.func,
  apiPlanDetails: PropTypes.object,
  apiPlanUuid: PropTypes.string,
  enableQuotaCheck: PropTypes.bool,
  setEnableQuotaCheck: PropTypes.func,
  enableRateLimitCheck: PropTypes.bool,
  setEnableRateLimitCheck: PropTypes.func,
}

export default OverView;